import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { TableStore } from "src/components/tables/TableStore";
import { ZUid } from "src/types/zUid";
import { cvtError, onError } from "src/common/onError";
import { msgQueueStore } from "src/components/MsgQueue";
import { loadObjectStates } from "src/pages/ManagementPage/Obj2Tab/roles/rolesApi";
import { ZObjState } from "src/types/ZObjState";
import { ZIdName } from "src/types/ZIdName";
import {
  changeEntitiesState,
  loadAvailableStates,
} from "src/pages/EntityCardPage/apiEntityCard";
import { loadObjectAttrinbutesAll } from "src/pages/ManagementPage/objectsApi";
import { getEditorInfo } from "src/common/attrEdit";
import { AColumn } from "src/components/tables/AsyncTable";
import {
  createBom3,
  deleteBom3,
  loadBom3Permissions,
  loadBom3Settings,
  loaderBom3,
} from "../apiBom3";
import { Bom3Filter, ZBom3, ZPermission } from "../ZBom3";
import { Bom3PositionStore } from "../Bom3Positions/Bom3PositionsStore";
import { ZBom3Settings } from "../ZBom3Settings";
import { Bom3CopyInStore } from "./copyIn";
import { Bom3CopyFromStore } from "./copyFrom";
import { createBom3SummaryColumns } from "./createBom3SummaryColumns";

export type Bom3Data = {
  modelEntityId: ZUid;
  modelObjectId: ZUid;
  tableStore: TableStore<ZBom3, Bom3Filter>;
  states: ZObjState[];
  settings: ZBom3Settings;
  sizeLineAttrNameId?: ZUid;
  totalPrecision?: number | null;
  permissions: ZPermission[];
};

export type Bom3EdFields = Pick<ZBom3, "name" | "sizeLineId">;

type Task = "changeState" | "create" | "delete";

export const bom3ControlStore = makeAutoObservable({
  init(modelEntityId: ZUid, objectId: ZUid, typesMap: Record<number, string>) {
    this.typesMap = typesMap;
    this.setPositions(null);
    this.setData({ status: "wait" });
    const load = async () => {
      const permissions = await loadBom3Permissions(objectId);
      const settings = await loadBom3Settings(objectId);
      const { objBom, objSizeLine, attBomTotalAmount } = settings;
      if (!objBom) throw Error("В настройках не указан объект BOM");
      let sizeLineAttrNameId: ZUid | undefined;
      if (objSizeLine) {
        // Поиск атрибута с названием для objSizeLine
        const attrs = await loadObjectAttrinbutesAll(objSizeLine);
        sizeLineAttrNameId = attrs.find(
          ({ fieldKey }) => fieldKey === "NAME",
        )?.id;
      }
      const states = await loadObjectStates(objBom, { translate: true });
      const tableStore = new TableStore<ZBom3, Bom3Filter>({
        rowKey: "id",
        fnLoad: loaderBom3(modelEntityId),
        selectionSettings: {
          selectionType: "checkbox",
        },
        resizableColumns: true,
        settingsKey: "Bom3Control",
      });

      let totalPrecision: number | null | undefined;
      if (attBomTotalAmount) {
        const attrs = await loadObjectAttrinbutesAll(objBom);
        const meta = attrs.find(({ id }) => id === attBomTotalAmount);
        const component = getEditorInfo(meta?.viewStyles)?.component;
        if (component?.editor === "InputNumber") {
          totalPrecision = component?.precision;
        }
      }

      const result = {
        modelEntityId,
        tableStore,
        states,
        settings,
        sizeLineAttrNameId,
        totalPrecision,
        modelObjectId: objectId,
        permissions,
      };
      this.setData({ status: "ready", result });
    };
    load().catch((e) => this.setData({ status: "error", error: cvtError(e) }));
  },
  done() {
    this.setData({ status: "none" });
    this.setPositions(null);
    this.closeCreate();
    this.closeEdit();
  },
  typesMap: {} as Record<number, string>,
  data: { status: "none" } as RemoteData<Bom3Data>,
  setData(newData: RemoteData<Bom3Data>) {
    this.data = newData;
  },
  get columns(): AColumn<ZBom3>[] {
    return createBom3SummaryColumns(this);
  },
  get selectedRows(): ZBom3[] {
    const { data } = this;
    if (data.status !== "ready") return [];
    return data.result.tableStore.selected;
  },
  get showTotalAmount(): boolean {
    const { data } = this;
    if (data.status !== "ready") return false;
    return !!data.result.settings.commonSumma;
  },
  get showSizeLine(): boolean {
    const { data } = this;
    if (data.status !== "ready") return false;
    return !!data.result.settings.sizeVariations;
  },
  makeTotalValue(srcTotalValue: number | null | undefined): string {
    if (typeof srcTotalValue !== "number") return "";
    const { data } = this;
    if (data.status !== "ready") return "";
    const { totalPrecision } = data.result;
    const totalValue = srcTotalValue;
    if (typeof totalPrecision === "number") {
      return totalValue.toFixed(totalPrecision);
    }
    return String(totalValue);

    // Была попытка использовать нормально форматированное число. Но аудитория пока не оценила 😁
    // return new Intl.NumberFormat(undefined, {
    //   minimumFractionDigits: totalPrecision,
    //   maximumFractionDigits: totalPrecision}
    // ).format(totalValue);
  },

  accessForSelectedRows(action: "DELETE" | "UPDATE"): boolean {
    const { data } = this;
    if (data.status !== "ready") return false;
    return data.result.tableStore.selected.every(({ permissions }) =>
      (permissions ?? []).includes(action),
    );
  },

  // Для перехода к показу позиций

  positions: null as Bom3PositionStore | null,
  setPositions(store: Bom3PositionStore | null) {
    this.positions = store;
  },
  openBomPositions(bom: ZBom3) {
    const { data } = this;
    if (data.status === "ready") {
      this.setPositions(
        new Bom3PositionStore(bom, this.typesMap, data.result.settings),
      );
    }
  },

  // ---- create

  currentRow: null as ZBom3 | null,
  onCreate() {
    this.currentRow = { id: "", name: "" };
  },
  apply(fields: Bom3EdFields) {
    const { currentRow, data, task } = this;
    if (!currentRow || data.status !== "ready" || task) return;
    const { modelEntityId } = data.result;
    this.execTask("create", async () => {
      const body: ZBom3 = { ...currentRow, ...fields };
      await createBom3(modelEntityId, body);
      this.closeCreate();
    });
  },
  closeCreate() {
    this.currentRow = null;
  },

  // ---- edit
  get msgEdit(): string {
    const { selectedRows } = this;
    if (selectedRows.length !== 1) return "Необходимо выбрать одну строку";
    if (!this.accessForSelectedRows("UPDATE"))
      return "Нет прав на редактирование";
    return "";
  },
  currentBom: null as ZBom3 | null,
  onEdit(row: ZBom3) {
    this.currentBom = row;
  },
  closeEdit() {
    this.currentBom = null;
  },
  onEditSuccess() {
    const { tableStore } = this;
    tableStore?.setSelected([]);
    tableStore?.reload().catch(onError);
    this.closeEdit();
  },

  // ---- delete
  get msgDelete(): string {
    const { selectedRows } = this;
    if (selectedRows.length === 0)
      return "Необходимо выбрать хотя бы одну строку";
    if (!this.accessForSelectedRows("DELETE")) return "Нет прав на удаление";
    return "";
  },
  deleteIds: [] as ZUid[],
  setDeleteIds(ids: ZUid[]) {
    this.deleteIds = ids;
  },
  doDelete() {
    const { deleteIds } = this;
    if (deleteIds.length === 0) return;
    this.execTask("delete", async () => {
      await deleteBom3(deleteIds);
      this.setDeleteIds([]);
      msgQueueStore.add({ type: "success", message: "Удаление выполнено" });
    });
  },

  get tableStore(): Bom3Data["tableStore"] | null {
    const { data } = this;
    return data.status === "ready" ? data.result.tableStore : null;
  },

  // --------- copy in
  get copyInMsg(): string {
    const { length } = this.selectedRows;
    if (length === 0) return "Необходимо выбрать хотя бы одну строку";
    return "";
  },
  get canCopyIn(): boolean {
    return !this.copyInMsg;
  },
  copyInStore: null as Bom3CopyInStore | null,
  setCopyInStore(copyStore: Bom3CopyInStore | null) {
    this.copyInStore = copyStore;
  },
  startCopyIn() {
    const { data } = this;
    if (data.status === "ready") {
      this.setCopyInStore(
        new Bom3CopyInStore({
          bomEntityIdsFrom: this.selectedRows.map(({ id }) => id),
          modelObjectId: data.result.modelObjectId,
          close: () => this.setCopyInStore(null),
          onFinish: () => {
            const { tableStore } = data.result;
            tableStore.setSelected([]);
            tableStore.load({ page: 0 }).catch(onError);
          },
        }),
      );
    }
  },

  // Copy from
  copyFromStore: null as Bom3CopyFromStore | null,
  setCopyFromStore(copyStore: Bom3CopyFromStore | null) {
    this.copyFromStore = copyStore;
  },
  startCopyFrom() {
    const { data } = this;
    if (data.status === "ready") {
      this.setCopyFromStore(
        new Bom3CopyFromStore({
          modelObjectId: data.result.modelObjectId,
          dstModelEntityId: data.result.modelEntityId,
          close: () => this.setCopyFromStore(null),
          onFinish: () => {
            data.result.tableStore.reload().catch(onError);
          },
        }),
      );
    }
  },

  // ------- state
  stateMode: false,
  setStateMode(flag: boolean) {
    this.stateMode = flag;
  },
  get changeStateMsg(): string {
    const statesCount = this.selectedStates.size;
    if (statesCount === 0)
      return "Необходимо выбрать хотя бы одну строку с состоянием";
    if (statesCount > 1)
      return "Состояния выбранных строк должны быть одинаковые";
    if (this.targetStates.length === 0)
      return "Нет ни одного состояния для переключения";
    return "";
  },
  get selectedStates(): Map<ZUid, string> {
    const result = new Map<ZUid, string>();
    this.tableStore?.selected?.forEach(({ state }) => {
      if (state) result.set(state.id, state.name);
    });
    return result;
  },
  get canChangeState(): boolean {
    return !this.changeStateMsg;
  },
  get currentState(): ZIdName | null {
    const list = Array.from(this.selectedStates);
    const first = list[0];
    if (first && list.length === 1) {
      return { id: first[0], name: first[1] };
    }
    return null;
  },
  get targetStates(): ZIdName[] {
    const { data, currentState } = this;
    const all: ZIdName[] = data.status === "ready" ? data.result.states : [];
    return all.filter(({ id }) => id !== currentState?.id);
  },
  doChangeState(stateId: string) {
    this.execTask("changeState", async () => {
      await changeEntitiesState(
        stateId,
        this.tableStore?.selected.map(({ id }) => id) ?? [],
      );
      this.setStateMode(false);
      msgQueueStore.add({ type: "success", message: "Изменение выполнено" });
    });
  },
  async loadStates(): Promise<ZIdName[]> {
    const { currentState, data } = this;
    if (!currentState) throw Error("Не определено исходное состояние");
    if (data.status !== "ready") throw Error("Нет данных");
    const objectId = data.result.settings.objBom;
    if (!objectId) throw Error("В настройках не указан объект BOM");
    const list = await loadAvailableStates(objectId, currentState.id, {
      translate: true,
    });
    return list.map(({ stateId, workflowName }) => ({
      id: stateId,
      name: workflowName,
    }));
  },

  // ------ tasks

  execTask(taskId: Task, work: () => Promise<void>) {
    this.setTask(taskId);
    const { data } = this;
    const tableStore = data.status === "ready" ? data.result.tableStore : null;
    work()
      .then(() => {
        tableStore?.setSelected([]);
        tableStore?.load({ page: 0 }).catch(onError);
      })
      .finally(() => this.setTask(null))
      .catch(onError);
  },
  task: null as Task | null,
  setTask(newTask: Task | null) {
    this.task = newTask;
  },
});
